import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Label,
} from "recharts";
import { BrandContext } from "../../App";
import theme from "../../Theme";

const PreviousResultsChart = ({ scenarioData = null }) => {
  const { brand, authToken, simulations, fetchRecentSimulationsForScenario } =
    React.useContext(BrandContext);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchChartSimulations = async () => {
      try {
        setLoading(true);
        let simulationsData = [];

        if (scenarioData?.id) {
          simulationsData = await fetchRecentSimulationsForScenario(
            scenarioData.id,
            5
          );
        } else {
          simulationsData = simulations?.recentWithEvaluation || [];
        }

        const formatted = simulationsData.slice(0, 5).map((sim, index) => {
          let score = 0;

          if (sim.evaluation && typeof sim.evaluation === "object") {
            if (
              sim.evaluation.total_score &&
              typeof sim.evaluation.total_score === "object" &&
              "value" in sim.evaluation.total_score &&
              !isNaN(parseFloat(sim.evaluation.total_score.value))
            ) {
              score = parseFloat(sim.evaluation.total_score.value) / 10;
            }
          }

          return {
            label: `${index + 1}`,
            score,
          };
        });

        setChartData(formatted);
      } catch (err) {
        console.error("Error fetching simulations:", err.message);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChartSimulations();
  }, [
    scenarioData?.id,
    simulations?.recentWithEvaluation,
    fetchRecentSimulationsForScenario,
  ]);

  const scenarioName =
    scenarioData?.name ||
    simulations?.recentWithEvaluation?.[0]?.scenario?.name ||
    "Scenario";

  return (
    <Box
      sx={{
        flexGrow: 1,
        padding: theme.customSpacing["3xl"],
        boxShadow:
          "0px 12px 16px -4px rgba(0, 91, 140, 0.10), 0px 4px 6px -2px rgba(0, 91, 140, 0.03), 0px 2px 2px -1px rgba(0, 91, 140, 0.04)",
        border: `2px solid`,
        borderColor: theme.palette.border.tertiary,
        borderRadius: "8px",
      }}
    >
      <Typography
        sx={{
          fontSize: theme.typography.textMD.fontSize,
          fontWeight: theme.typography.fontWeightMedium,
          lineHeight: theme.typography.textMD.lineHeight,
        }}
        gutterBottom
      >
        Previous Simulation Challenge Results:
      </Typography>
      <Typography
        sx={{
          fontSize: theme.typography.textMD.fontSize,
          fontWeight: theme.typography.fontWeightLight,
          lineHeight: theme.typography.textMD.lineHeight,
          fontStyle: "italic",
          color: theme.palette.text.tertiary,
        }}
        gutterBottom
      >
        {scenarioName}
      </Typography>

      {loading ||
        (simulations?.recentEvaluationLoading && (
          <Typography
            sx={{
              display: "flex",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              margin: "50px 0",
            }}
          >
            Loading...
          </Typography>
        ))}
      {chartData.length === 0 && !loading && (
        <Typography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "50px 0",
          }}
        >
          Try your first{" "}
          {scenarioData?.has_scoring_engine
            ? "Simulation Challenge"
            : "Skill Session"}{" "}
          to get some results!
        </Typography>
      )}
      {chartData.length > 0 && !loading && (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 0, left: 20, bottom: 30 }}
          >
            {/* Horizontal grid lines only */}
            <CartesianGrid
              stroke={theme.palette.border.tertiary}
              vertical={false}
            />

            {/* Y Axis */}
            <YAxis
              domain={[0, 10]}
              ticks={[0, 2, 4, 6, 8, 10]}
              tick={{
                fill: theme.palette.text.tertiary,
                fontSize: 14,
              }}
              axisLine={false}
              tickLine={false}
            >
              <Label
                value="SCORE"
                angle={-90}
                position="insideLeft"
                dy={30}
                dx={-10}
                style={{
                  fill: theme.palette.text.tertiary,
                  fontSize: theme.typography.textMD.fontSize,
                  fontWeight: theme.typography.fontWeightRegular,
                  lineHeight: theme.typography.textMD.lineHeight,
                }}
              />
            </YAxis>

            {/* X Axis */}
            <XAxis
              dataKey="label"
              tick={{ fill: theme.palette.text.tertiary, fontSize: 14 }}
              axisLine={false}
              tickLine={false}
            >
              <Label
                value="Last 5 Results"
                position="bottom"
                offset={0}
                style={{
                  fill: theme.palette.text.tertiary,
                  fontSize: theme.typography.textMD.fontSize,
                  fontWeight: theme.typography.fontWeightRegular,
                  lineHeight: theme.typography.textMD.lineHeight,
                }}
              />
            </XAxis>

            {/* Bars */}
            <Bar
              dataKey="score"
              fill={theme.palette.foreground.badge_icon}
              radius={[8, 8, 0, 0]} // Rounded top corners
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default PreviousResultsChart;
